// src/pages/DashboardCombustible.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import { getDashboardCombustible } from "../helpers/rutaViajes";

/* ==================================================
   FECHAS
================================================== */

const todayLocalISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const firstDayMonthISO = () => {
  const d = new Date();
  d.setDate(1);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

/* ==================================================
   FORMATOS
================================================== */

const fmtNumber = (n, dec = 2) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });

const money = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fechaCorta = (value) => {
  if (!value) return "-";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getCamionLabel = (c) => {
  if (!c) return "-";

  const camion = c.camion || "-";
  const patente = c.patente || "-";

  return `${camion} - ${patente}`;
};

/* ==================================================
   KPI
================================================== */

function KpiCard({ titulo, valor, subtitulo, color = "#2563eb", tipo = "texto" }) {
  const mostrar =
    tipo === "money"
      ? money(valor)
      : tipo === "litros"
      ? `${fmtNumber(valor)} L`
      : tipo === "precio"
      ? `${money(valor)} / L`
      : tipo === "consumo"
      ? valor == null
        ? "-"
        : `${fmtNumber(valor)} km/L`
      : tipo === "consumoLKm"
      ? valor == null
        ? "-"
        : `${fmtNumber(valor)} L/km`
      : fmtNumber(valor, 0);

  return (
    <Card
      className="shadow-sm h-100"
      style={{
        background: "#ffffff",
        color: "#1f2937",
        border: "1px solid #dbe3ef",
        borderLeft: `5px solid ${color}`,
        borderRadius: 12,
      }}
    >
      <Card.Body>
        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          {titulo}
        </div>

        <div style={{ fontSize: 26, fontWeight: 800, color }}>
          {mostrar}
        </div>

        <div style={{ fontSize: 12, color: "#6b7280" }}>
          {subtitulo}
        </div>
      </Card.Body>
    </Card>
  );
}

/* ==================================================
   BARRA RESUMEN
================================================== */

function BarraSimple({ label, valor, max, detalle }) {
  const pct = max > 0 && valor > 0 ? Math.max((valor / max) * 100, 2) : 0;

  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 4,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1f2937" }}>
          {label}
        </div>

        <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
          {detalle}
        </div>
      </div>

      <div
        style={{
          background: "#e5e7eb",
          borderRadius: 8,
          overflow: "hidden",
          height: 13,
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            background: "#2563eb",
            height: "100%",
            borderRadius: 8,
          }}
        />
      </div>
    </div>
  );
}

const cardWhiteStyle = {
  background: "#ffffff",
  color: "#1f2937",
  border: "1px solid #dbe3ef",
  borderRadius: 12,
};

/* ==================================================
   DASHBOARD
================================================== */

export default function DashboardCombustible() {
  const [desde, setDesde] = useState(firstDayMonthISO());
  const [hasta, setHasta] = useState(todayLocalISO());

  const [camionId, setCamionId] = useState("");
  const [choferId, setChoferId] = useState("");

  const [resumen, setResumen] = useState({
    cantidadViajes: 0,
    cantidadCargas: 0,
    totalLitros: 0,
    totalMonto: 0,
    precioPromedioLitro: 0,
    totalKm: 0,
    litrosConConsumo: 0,
    consumoPromedioKmL: null,
    consumoPromedioLKm: null,
  });

  const [detalle, setDetalle] = useState([]);
  const [porCamion, setPorCamion] = useState([]);
  const [porChofer, setPorChofer] = useState([]);
  const [camiones, setCamiones] = useState([]);
  const [choferes, setChoferes] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const cargarDashboard = async (filtrosExtra = null) => {
    setLoading(true);
    setError("");

    try {
      const filtros = filtrosExtra || {
        desde,
        hasta,
        camionId,
        choferId,
      };

      const resp = await getDashboardCombustible(filtros);

      if (!resp?.ok) {
        throw new Error(
          resp?.error?.error ||
            resp?.error?.message ||
            resp?.error ||
            "No se pudo cargar el dashboard."
        );
      }

      setResumen(resp.resumen || {});
      const detalleOrdenado = Array.isArray(resp.detalle)
          ? [...resp.detalle].sort(
              (a, b) =>
                (Number(b.kmTablero) || 0) -
                (Number(a.kmTablero) || 0)
            )
          : [];
      setDetalle(detalleOrdenado);
      setPorCamion(Array.isArray(resp.porCamion) ? resp.porCamion : []);
      setPorChofer(Array.isArray(resp.porChofer) ? resp.porChofer : []);
      setCamiones(Array.isArray(resp.camiones) ? resp.camiones : []);
      setChoferes(Array.isArray(resp.choferes) ? resp.choferes : []);
    } catch (err) {
      console.error("❌ Error dashboard combustible:", err);

      setResumen({
        cantidadViajes: 0,
        cantidadCargas: 0,
        totalLitros: 0,
        totalMonto: 0,
        precioPromedioLitro: 0,
        totalKm: 0,
        litrosConConsumo: 0,
        consumoPromedioKmL: null,
        consumoPromedioLKm: null,
      });

      setDetalle([]);
      setPorCamion([]);
      setPorChofer([]);

      setError(err?.message || "No se pudo cargar el dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDashboard();
    // eslint-disable-next-line
  }, []);

  const maxMontoCamion = useMemo(() => {
    return Math.max(...porCamion.map((x) => Number(x.monto || 0)), 1);
  }, [porCamion]);

  const maxMontoChofer = useMemo(() => {
    return Math.max(...porChofer.map((x) => Number(x.monto || 0)), 1);
  }, [porChofer]);

  const topCamiones = useMemo(() => {
    return [...porCamion].sort((a, b) => Number(b.monto || 0) - Number(a.monto || 0)).slice(0, 8);
  }, [porCamion]);

  const topChoferes = useMemo(() => {
    return [...porChofer].sort((a, b) => Number(b.monto || 0) - Number(a.monto || 0)).slice(0, 8);
  }, [porChofer]);

  const limpiarFiltros = () => {
    const filtros = {
      desde: firstDayMonthISO(),
      hasta: todayLocalISO(),
      camionId: "",
      choferId: "",
    };

    setDesde(filtros.desde);
    setHasta(filtros.hasta);
    setCamionId("");
    setChoferId("");

    cargarDashboard(filtros);
  };

  return (
    <Container
      fluid
      className="py-3"
      style={{ background: "#ffffff", minHeight: "100vh" }}
    >
      {/* CABECERA */}

      <div
        className="d-flex flex-wrap justify-content-between align-items-end mb-3"
        style={{ color: "#1f2937", gap: 12 }}
      >
        <div>
          <h3 className="mb-1">Dashboard Combustible</h3>

          <small style={{ color: "#6b7280" }}>
            Consumo, costo por litro, camión, dominio y chofer entre fechas
          </small>
        </div>

        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={limpiarFiltros}
            disabled={loading}
          >
            Limpiar
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={() => cargarDashboard()}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-1" />
                Buscando…
              </>
            ) : (
              "Buscar"
            )}
          </Button>
        </div>
      </div>

      {/* FILTROS */}

      <Card className="mb-3" style={cardWhiteStyle}>
        <Card.Body>
          <Row>
            <Col xs={12} md={3} lg={2} className="mb-2">
              <Form.Label>Desde</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </Col>

            <Col xs={12} md={3} lg={2} className="mb-2">
              <Form.Label>Hasta</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
              />
            </Col>

            <Col xs={12} md={3} lg={4} className="mb-2">
              <Form.Label>Camión / Dominio</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                value={camionId}
                onChange={(e) => setCamionId(e.target.value)}
              >
                <option value="">Todos los camiones</option>

                {camiones.map((c) => (
                  <option key={c._id} value={c._id}>
                    {getCamionLabel(c)}
                  </option>
                ))}
              </Form.Control>
            </Col>

            <Col xs={12} md={3} lg={4} className="mb-2">
              <Form.Label>Chofer</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                value={choferId}
                onChange={(e) => setChoferId(e.target.value)}
              >
                <option value="">Todos los choferes</option>

                {choferes.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.nombre}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error ? (
        <Alert variant="danger" className="py-2">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <div className="mt-2">Cargando dashboard...</div>
        </div>
      ) : (
        <>
          {/* KPIS */}

          <Row
              className="mb-3"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                gap: 12,
                marginLeft: 0,
                marginRight: 0,
              }}
            >
            <Col className="mb-3">
              <KpiCard
                titulo="Viajes"
                valor={resumen.cantidadViajes}
                subtitulo="Viajes con cargas"
                color="#2563eb"
              />
            </Col>

            <Col className="mb-3">
              <KpiCard
                titulo="Cargas"
                valor={resumen.cantidadCargas}
                subtitulo="Registros de combustible"
                color="#6f42c1"
              />
            </Col>

            <Col className="mb-3">
              <KpiCard
                titulo="Litros"
                valor={resumen.totalLitros}
                tipo="litros"
                subtitulo="Total cargado"
                color="#0ea5e9"
              />
            </Col>

            <Col className="mb-3">
              <KpiCard
                titulo="Combustible"
                valor={resumen.totalMonto}
                tipo="money"
                subtitulo="Total gastado"
                color="#ef4444"
              />
            </Col>

            <Col className="mb-3">
              <KpiCard
                titulo="Precio litro"
                valor={resumen.precioPromedioLitro}
                tipo="precio"
                subtitulo="Promedio ponderado"
                color="#f97316"
              />
            </Col>

          <Col className="mb-3">
            <KpiCard
              titulo="Rendimiento"
              valor={resumen.consumoPromedioKmL}
              tipo="consumo"
              subtitulo={`${fmtNumber(resumen.totalKm, 0)} km recorridos`}
              color="#22c55e"
            />
          </Col>

          <Col className="mb-3">
            <KpiCard
              titulo="Consumo"
              valor={resumen.consumoPromedioLKm}
              tipo="consumoLKm"
              subtitulo={`${fmtNumber(resumen.litrosConConsumo)} L para el cálculo`}
              color="#22c55e"
            />
          </Col>
          </Row>

          {/* RESUMENES */}

          <Row className="mb-3">
            <Col lg={6} className="mb-3">
              <Card className="shadow-sm h-100" style={cardWhiteStyle}>
                <Card.Body>
                  <h6 className="mb-3">Gasto por camión</h6>

                  {topCamiones.length === 0 ? (
                    <div className="text-muted">Sin datos para el filtro seleccionado.</div>
                  ) : (
                    topCamiones.map((r) => (
                      <BarraSimple
                        key={r.key}
                        label={`${r.camion || "-"} - ${r.patente || "-"}`}
                        valor={Number(r.monto || 0)}
                        max={maxMontoCamion}
                        detalle={`${money(r.monto)} | ${fmtNumber(r.litros)} L | ${fmtNumber(
                          r.precioPromedioLitro
                        )} $/L`}
                      />
                    ))
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-3">
              <Card className="shadow-sm h-100" style={cardWhiteStyle}>
                <Card.Body>
                  <h6 className="mb-3">Gasto por chofer</h6>

                  {topChoferes.length === 0 ? (
                    <div className="text-muted">Sin datos para el filtro seleccionado.</div>
                  ) : (
                    topChoferes.map((r) => (
                      <BarraSimple
                        key={r.key}
                        label={r.chofer || "-"}
                        valor={Number(r.monto || 0)}
                        max={maxMontoChofer}
                        detalle={`${money(r.monto)} | ${fmtNumber(r.litros)} L | ${fmtNumber(
                          r.precioPromedioLitro
                        )} $/L`}
                      />
                    ))
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* TABLAS RESUMEN */}

          <Row className="mb-3">
            <Col lg={6} className="mb-3">
              <Card className="shadow-sm" style={cardWhiteStyle}>
                <Card.Body>
                  <h6>Resumen por camión</h6>

                  <div className="table-responsive">
                    <Table size="sm" hover bordered className="mb-0">
                      <thead style={{ background: "#eef3f8", color: "#1f2937" }}>
                        <tr>
                          <th>Camión</th>
                          <th>Dominio</th>
                          <th className="text-right">Viajes</th>
                          <th className="text-right">Cargas</th>
                          <th className="text-right">Litros</th>
                          <th className="text-right">Monto</th>
                          <th className="text-right">$/L</th>
                          <th className="text-right">Km/L</th>
                        </tr>
                      </thead>

                      <tbody>
                        {porCamion.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center text-muted">
                              Sin datos.
                            </td>
                          </tr>
                        ) : (
                          porCamion.map((r) => (
                            <tr key={r.key}>
                              <td>{r.camion || "-"}</td>
                              <td>{r.patente || "-"}</td>
                              <td className="text-right">{r.viajes}</td>
                              <td className="text-right">{r.cargas}</td>
                              <td className="text-right">{fmtNumber(r.litros)}</td>
                              <td className="text-right">{money(r.monto)}</td>
                              <td className="text-right">{money(r.precioPromedioLitro)}</td>
                              <td className="text-right">
                                {Number(r.consumoPromedioKmL || 0) > 0
                                  ? fmtNumber(r.consumoPromedioKmL)
                                  : "-"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-3">
              <Card className="shadow-sm" style={cardWhiteStyle}>
                <Card.Body>
                  <h6>Resumen por chofer</h6>

                  <div className="table-responsive">
                    <Table size="sm" hover bordered className="mb-0">
                      <thead style={{ background: "#eef3f8", color: "#1f2937" }}>
                        <tr>
                          <th>Chofer</th>
                          <th className="text-right">Viajes</th>
                          <th className="text-right">Cargas</th>
                          <th className="text-right">Litros</th>
                          <th className="text-right">Monto</th>
                          <th className="text-right">$/L</th>
                          <th className="text-right">Km/L</th>
                        </tr>
                      </thead>

                      <tbody>
                        {porChofer.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center text-muted">
                              Sin datos.
                            </td>
                          </tr>
                        ) : (
                          porChofer.map((r) => (
                            <tr key={r.key}>
                              <td>{r.chofer || "-"}</td>
                              <td className="text-right">{r.viajes}</td>
                              <td className="text-right">{r.cargas}</td>
                              <td className="text-right">{fmtNumber(r.litros)}</td>
                              <td className="text-right">{money(r.monto)}</td>
                              <td className="text-right">{money(r.precioPromedioLitro)}</td>
                              <td className="text-right">
                                {Number(r.consumoPromedioKmL || 0) > 0
                                  ? fmtNumber(r.consumoPromedioKmL)
                                  : "-"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* DETALLE */}

          <Card className="shadow-sm" style={cardWhiteStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Detalle de cargas de combustible</h6>

                <small className="text-muted">
                  {detalle.length} registros
                </small>
              </div>

              <div className="table-responsive">
                <Table size="sm" hover bordered className="mb-0">
                  <thead style={{ background: "#eef3f8", color: "#1f2937" }}>
                    <tr>
                      <th>Fecha</th>
                      <th>Nro Viaje</th>
                      <th>Camión</th>
                      <th>Dominio</th>
                      <th>Chofer</th>
                      <th>Estación</th>
                      <th>Localidad</th>
                      <th className="text-right">Litros</th>
                      <th className="text-right">Monto</th>
                      <th className="text-right">$/L</th>
                      <th className="text-right">Km tablero</th>
                      <th className="text-right">Km tramo</th>
                      <th className="text-right">Km/L</th>
                      <th>Pago</th>
                    </tr>
                  </thead>

                  <tbody>
                    {detalle.length === 0 ? (
                      <tr>
                        <td colSpan="14" className="text-center text-muted py-3">
                          Sin cargas para el filtro seleccionado.
                        </td>
                      </tr>
                    ) : (
                      detalle.map((r, i) => (
                        <tr key={`${r.viajeId}-${r.fecha}-${i}`}>
                          <td style={{ whiteSpace: "nowrap" }}>{fechaCorta(r.fecha)}</td>
                          <td>{r.nroViaje || "-"}</td>
                          <td>{r.camion || "-"}</td>
                          <td>{r.patente || "-"}</td>
                          <td>{r.chofer || "-"}</td>
                          <td>{r.estacion || "-"}</td>
                          <td>{r.localidad || "-"}</td>
                          <td className="text-right">{fmtNumber(r.litros)}</td>
                          <td className="text-right">{money(r.monto)}</td>
                          <td className="text-right">{money(r.precioLitro)}</td>
                          <td className="text-right">
                            {Number(r.kmTablero || 0) > 0 ? fmtNumber(r.kmTablero, 0) : "-"}
                          </td>
                          <td className="text-right">
                            {Number(r.kmRecorrido || 0) > 0 ? fmtNumber(r.kmRecorrido, 0) : "-"}
                          </td>
                          <td className="text-right">
                            {Number(r.consumoKmL || 0) > 0 ? fmtNumber(r.consumoKmL) : "-"}
                          </td>
                          <td>{r.metodoPago || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
}